// src/components/button/Button.tsx
import { cva } from "class-variance-authority";
import * as React from "react";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/button/Button.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var buttonVariants = cva(
  "group flex flex-row gap-2 z-10 items-center justify-start rounded-md cursor-pointer px-4 ring-offset-background focus:outline-none focus-visible:ring-2 focus:ring-0 focus-visible:ring-offset-2 disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "border-transparent bg-primary hover:bg-primary-hover active:bg-primary-hover shadow-inner-button-primary",
        secondary: "border-transparent bg-secondary hover:bg-secondary-hover cursor-pointer active:opacity-80 shadow-inner-button-secondary",
        tertiary: "border-transparent bg-card hover:bg-color-bg-card-hover focus:ring-focused-text active:opacity-80",
        quaternary: "border-transparent bg-quaternary hover:bg-quaternary-hover focus:ring-focused-text active:opacity-80",
        outline: "border border-color-bg-secondary bg-white hover:bg-color-bg-elevated hover:border-color-border-hover focus:ring-focused-text active:opacity-80",
        ghost: "border-transparent hover:bg-color-bg-elevated focus:ring-focused-text active:opacity-80",
        destructive: "border-transparent bg-error hover:bg-error-hover focus:ring-focusRingError active:opacity-80 shadow-inner-button-error",
        success: "border-transparent bg-success hover:bg-success-hover active:opacity-80 shadow-inner-button-success",
        warning: "border-transparent bg-warning hover:bg-warning-hover focus:ring-focused-text active:opacity-80 shadow-inner-button-warning"
      },
      size: {
        md: "h-10 py-2 w-fit",
        xs: "h-7 w-fit px-2",
        sm: "h-9 w-fit",
        lg: "h-11 w-fit",
        xl: "h-12 w-fit",
        mdFull: "h-10 py-2 w-full",
        smFull: "h-9 w-full",
        lgFull: "h-11 w-full",
        xlFull: "h-12 w-full"
      },
      active: {
        true: "",
        false: ""
      }
    },
    compoundVariants: [
      { variant: "primary", active: true, className: "bg-primary-hover border-2 border-primary-border" },
      { variant: "secondary", active: true, className: "bg-secondary-hover" },
      { variant: "tertiary", active: true, className: "bg-color-bg-card-hover" },
      { variant: "quaternary", active: true, className: "bg-navy-hover" },
      { variant: "outline", active: true, className: "bg-color-bg-elevated" },
      { variant: "ghost", active: true, className: "bg-color-bg-elevated" },
      { variant: "destructive", active: true, className: "bg-schemes-error" },
      { variant: "success", active: true, className: "bg-success-hover" },
      { variant: "warning", active: true, className: "bg-warning-hover" }
    ],
    defaultVariants: {
      variant: "primary",
      size: "md"
    }
  }
);
var buttonTextVariants = cva("font-medium", {
  variants: {
    variant: {
      primary: "text-white",
      secondary: "text-white",
      tertiary: "text-color-text",
      outline: "text-color-text",
      ghost: "text-text-medium",
      destructive: "text-text-light",
      quaternary: "text-white",
      success: "text-text-light",
      warning: "text-text-light"
    },
    size: {
      xs: "text-sm font-normal",
      md: "text-sm",
      sm: "text-sm",
      lg: "text-base",
      xl: "text-base",
      mdFull: "text-sm",
      smFull: "text-sm",
      lgFull: "text-base",
      xlFull: "text-base"
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "md"
  }
});
var iconVariants = cva("", {
  variants: {
    variant: {
      primary: "text-white",
      secondary: "text-text-dark",
      tertiary: "text-color-text",
      quaternary: "text-white",
      outline: "text-color-text",
      ghost: "text-text-medium group-hover:text-background",
      destructive: "text-text-light",
      success: "text-text-light",
      warning: "text-text-light"
    }
  },
  defaultVariants: {
    variant: "primary"
  }
});
var Button = React.forwardRef(
  ({ className, variant, size, children, icon, active, disabled, iconDirection = "left", ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        className: cn(buttonVariants({ variant, size, active }), className, disabled, !disabled && "group"),
        type: "button",
        disabled,
        ...props,
        children: [
          iconDirection === "left" && icon && React.cloneElement(icon, {
            className: cn(iconVariants({ variant }), icon.props?.className)
          }),
          /* @__PURE__ */ jsx("div", { className: buttonTextVariants({ variant, size }), children }),
          iconDirection === "right" && icon && React.cloneElement(icon, {
            className: cn(iconVariants({ variant }), icon.props?.className)
          })
        ]
      }
    );
  }
);
Button.displayName = "Button";

// src/components/searchInput/SearchInput.tsx
import { View, TextInput } from "react-native";
import { styled } from "nativewind";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var SearchInput = ({
  placeholder,
  value,
  handleSearchInputChange,
  className,
  iconLeft,
  iconRight
}) => {
  const StyledComponent = styled(View);
  const StyledTextInput = styled(TextInput);
  const StyledView = styled(View);
  return /* @__PURE__ */ jsxs2(
    StyledComponent,
    {
      className: cn(
        "relative flex-row items-center h-10 px-4 bg-card rounded-lg !w-96",
        className
      ),
      children: [
        iconLeft && /* @__PURE__ */ jsx2(StyledView, { children: iconLeft }),
        /* @__PURE__ */ jsx2(
          StyledTextInput,
          {
            className: cn(
              "w-full h-full px-2 text-sm text-color-lightmode-info-gray rounded-lg focus:outline-none focus:border-transparent",
              className
            ),
            placeholder,
            value,
            onChangeText: handleSearchInputChange
          }
        ),
        iconRight && /* @__PURE__ */ jsx2(StyledView, { children: iconRight })
      ]
    }
  );
};

// src/components/Card.tsx
import { View as View2, StyleSheet } from "react-native";
import { jsx as jsx3 } from "react/jsx-runtime";
var Card = ({ children, style }) => {
  return /* @__PURE__ */ jsx3(View2, { style: [styles.card, style], children });
};
var styles = StyleSheet.create({
  card: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  }
});

// src/components/toggleSwitch/ToggleSwitch.tsx
import { View as View3, TouchableOpacity } from "react-native";
import { jsx as jsx4 } from "react/jsx-runtime";
var ToggleSwitch = ({ value, handleToggle }) => {
  return /* @__PURE__ */ jsx4(
    TouchableOpacity,
    {
      onPress: handleToggle,
      style: {
        width: 44,
        height: 24,
        borderRadius: 12,
        backgroundColor: value ? "#555CF3" : "#878B95",
        justifyContent: value ? "flex-end" : "flex-start",
        padding: 2,
        position: "relative"
      },
      children: /* @__PURE__ */ jsx4(
        View3,
        {
          style: {
            width: 20,
            height: 20,
            borderRadius: 10,
            backgroundColor: "#fff",
            position: "absolute",
            top: 2,
            left: value ? 22 : 2
          }
        }
      )
    }
  );
};

// src/components/progressBar/ProgressBar.tsx
import clsx2 from "clsx";

// src/components/text.tsx
import * as React2 from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var TextClassContext = React2.createContext(void 0);
var Text = React2.forwardRef(({ className, asChild = false, ...props }, ref) => {
  const textClass = React2.useContext(TextClassContext);
  const Component = asChild ? "span" : "span";
  return /* @__PURE__ */ jsx5(Component, { className: cn("text-base text-foreground select-text", textClass, className), ref, ...props });
});
Text.displayName = "Text";

// src/components/progressBar/ProgressBar.tsx
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var ProgressBar = ({
  progress,
  label,
  unit,
  rangeOptions = "defaultRanges",
  rangeValues,
  isStatusCard = false,
  className
}) => {
  const numericProgress = isNaN(+progress) ? 0 : Math.max(0, Math.min(100, +progress));
  let progressBarColor = "";
  let progressBarShadow = "";
  if (rangeOptions === "customRanges" && rangeValues) {
    const { greenRangeMin, greenRangeMax, yellowRangeMin, yellowRangeMax, redRangeMin, redRangeMax } = rangeValues;
    if (numericProgress >= greenRangeMin && numericProgress <= greenRangeMax && greenRangeMin <= greenRangeMax) {
      progressBarColor = "bg-green-500";
      progressBarShadow = "shadow-innner-button-success";
    } else if (numericProgress >= yellowRangeMin && numericProgress <= yellowRangeMax && yellowRangeMin <= yellowRangeMax) {
      progressBarColor = "bg-amber-500";
      progressBarShadow = "shadow-inner-button-warning";
    } else if (numericProgress >= redRangeMin && numericProgress <= redRangeMax && redRangeMin <= redRangeMax) {
      progressBarColor = "bg-red-500";
      progressBarShadow = "shadow-inner-button-error";
    } else {
      progressBarColor = "bg-red-500";
      progressBarShadow = "shadow-inner-button-error";
    }
  } else {
    if (numericProgress < 15) {
      progressBarColor = "bg-red-500";
      progressBarShadow = "shadow-inner-button-error";
    } else if (numericProgress < 50) {
      progressBarColor = "bg-amber-500";
      progressBarShadow = "shadow-inner-button-warning";
    } else {
      progressBarColor = "bg-green-500";
      progressBarShadow = "shadow-inner-button-success";
    }
  }
  return /* @__PURE__ */ jsxs3("div", { className: cn("flex flex-col w-full justify-center gap-4", className), children: [
    /* @__PURE__ */ jsxs3("div", { className: "flex flex-row w-full justify-between items-center gap-2", children: [
      /* @__PURE__ */ jsx6(
        Text,
        {
          className: clsx2("text-text-light dark:text-text-dark !leading-none", {
            "text-xs": !isStatusCard,
            "text-md": isStatusCard
          }),
          children: label && label
        }
      ),
      /* @__PURE__ */ jsx6(
        Text,
        {
          className: clsx2("font-medium text-sm text-info-text-light dark:text-info-text-dark !leading-none", {
            "text-md": isStatusCard
          }),
          children: `${progress ?? 0} ${unit}`
        }
      )
    ] }),
    /* @__PURE__ */ jsx6("div", { className: "w-full rounded-sm dark:bg-slate-900 bg-slate-500 shadow-progress-bar", children: /* @__PURE__ */ jsx6(
      "div",
      {
        className: cn("h-2 rounded-sm", progressBarColor, progressBarShadow),
        style: { width: `${numericProgress}%` }
      }
    ) })
  ] });
};

// src/components/ui/text.tsx
import * as React3 from "react";
import { jsx as jsx7 } from "react/jsx-runtime";
var TextClassContext2 = React3.createContext(void 0);
var Text2 = React3.forwardRef(({ className, asChild = false, ...props }, ref) => {
  const textClass = React3.useContext(TextClassContext2);
  const Component = asChild ? "span" : "span";
  return /* @__PURE__ */ jsx7(Component, { className: cn("text-base text-foreground select-text", textClass, className), ref, ...props });
});
Text2.displayName = "Text";

// src/components/statsCard/StatsCard.tsx
import { jsx as jsx8, jsxs as jsxs4 } from "react/jsx-runtime";
var statsCardVariants = (variant) => cn(
  "py-2 lg:py-5 px-4 xl:px-8 flex gap-4 flex-1 rounded-lg flex-wrap bg-background-wrapper-light dark:bg-background-wrapper-dark shadow-deep-light dark:shadow-deep-dark"
);
var statsCardTextVariants = (variant) => cn(variant === "active" ? "text-foreground" : "text-rockBlue");
var StatsCard = ({ variant = "primary", textLeft, textRight, className, ...props }) => {
  return /* @__PURE__ */ jsx8(TextClassContext2.Provider, { value: statsCardTextVariants(variant), children: /* @__PURE__ */ jsx8("div", { className: cn(statsCardVariants(variant), className), ...props, children: /* @__PURE__ */ jsxs4("div", { className: "items-center justify-between gap-4 flex-1 2xl:flex-row flex", children: [
    /* @__PURE__ */ jsx8(Text2, { className: "text-sm text-center", children: textLeft }),
    /* @__PURE__ */ jsx8(Text2, { className: "text-2xl font-semibold text-text", children: textRight })
  ] }) }) });
};

// src/components/ui/badge.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx9 } from "react/jsx-runtime";
var badgeVariants = cva2(
  "w-fit flex box-border justify-center items-center rounded-md border border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border border-white/10",
  {
    variants: {
      variant: {
        primary: [],
        secondary: [],
        error: [],
        success: [],
        warning: [],
        info: [],
        darkGrey: []
      },
      fill: {
        outline: [],
        solid: [],
        soft: [],
        none: []
      },
      badgeSize: {
        sm: "px-2 py-0.5 h-5",
        md: "px-2 py-0.5 h-6",
        lg: "px-3 py-1 h-7",
        xl: "px-3 py-1 h-10"
      }
    },
    compoundVariants: [
      { variant: "primary", fill: "solid", className: "bg-primaryBadge" },
      { variant: "primary", fill: "outline", className: "bg-foreground border-primaryBadge-border" },
      { variant: "success", fill: "solid", className: "bg-green-500" },
      { variant: "success", fill: "soft", className: "bg-success-muted" },
      { variant: "success", fill: "outline", className: "border-success-border" },
      { variant: "error", fill: "solid", className: "bg-red-500" },
      { variant: "error", fill: "soft", className: "bg-error-muted" },
      { variant: "error", fill: "outline", className: "border-error-border" },
      { variant: "warning", fill: "solid", className: "bg-amber-500" },
      { variant: "warning", fill: "soft", className: "bg-warning-muted" },
      { variant: "warning", fill: "outline", className: "border-warning-border" },
      { variant: "info", fill: "soft", className: "bg-info-muted" },
      { variant: "info", fill: "outline", className: "bg-foreground border-info-border" },
      { variant: "darkGrey", fill: "solid", className: "bg-background-gray-badge" },
      { variant: "darkGrey", fill: "outline", className: "border-badge-gray-outline-border" }
    ],
    defaultVariants: {
      variant: "primary",
      fill: "solid",
      badgeSize: "md"
    }
  }
);
var badgeTextVariants = cva2("font-medium !leading-none", {
  variants: {
    variant: {
      primary: [],
      secondary: [],
      error: [],
      success: [],
      warning: [],
      info: [],
      darkGrey: []
    },
    fill: {
      outline: [],
      solid: [],
      soft: [],
      none: []
    },
    badgeSize: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-sm",
      xl: "text-sm"
    }
  },
  compoundVariants: [
    { variant: "success", fill: "solid", className: "text-black" },
    { variant: "success", fill: "none", className: "text-success" },
    { variant: "success", fill: ["soft", "outline"], className: "text-success-text" },
    { variant: "error", fill: "solid", className: "text-white" },
    { variant: "error", fill: "none", className: "text-error" },
    { variant: "error", fill: ["soft", "outline"], className: "text-error-text" },
    { variant: "warning", fill: "solid", className: "text-black" },
    { variant: "warning", fill: "none", className: "text-warning" },
    { variant: "warning", fill: ["soft", "outline"], className: "text-warning-text" },
    { variant: "info", fill: ["soft", "outline"], className: "text-info-text" },
    { variant: "primary", fill: "solid", className: "text-black" },
    { variant: "primary", fill: "none", className: "text-foreground" },
    { variant: "primary", fill: "outline", className: "text-primaryBadge-text" },
    { variant: "darkGrey", fill: "solid", className: "text-white" },
    { variant: "darkGrey", fill: "outline", className: "text-badge-gray-outline-text" }
  ],
  defaultVariants: {
    variant: "primary",
    fill: "solid",
    badgeSize: "md"
  }
});
var Badge = ({ className, variant, fill, badgeSize, children, asChild, ...props }) => {
  const Component = asChild ? "span" : "div";
  return /* @__PURE__ */ jsx9(TextClassContext.Provider, { value: badgeTextVariants({ variant, fill, badgeSize }), children: /* @__PURE__ */ jsx9(Component, { className: cn(badgeVariants({ variant, fill, badgeSize }), className), ...props, children: /* @__PURE__ */ jsx9(Text, { children }) }) });
};

// src/components/fudCard/FudCard.tsx
import clsx3 from "clsx";

// src/components/iconButton/IconButton.tsx
import { cva as cva3 } from "class-variance-authority";
import * as React4 from "react";
import { jsx as jsx10 } from "react/jsx-runtime";
var iconButtonVariants = cva3(
  "hover:scale-[1.025] group flex items-center justify-center rounded-md cursor-pointer ring-offset-background focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ease-in-out disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "border-transparent bg-primary hover:bg-secondary focus:ring-focused-text text-white shadow-inner-button-primary",
        secondary: "border-transparent bg-secondary hover:bg-secondary-hover cursor-pointer active:opacity-80 shadow-inner-button-secondary",
        tertiary: "border-transparent bg-background-wrapper-light dark:bg-background-wrapper-dark hover:bg-background-wrapper-light-hover dark:hover:bg-background-wrapper-dark-hover focus:ring-focused-text shadow-inner-button-light dark:shadow-inner-button-dark",
        outline: "border border-color-bg-secondary bg-white hover:bg-color-bg-elevated focus:ring-focused-text",
        ghost: "border-transparent hover:bg-color-bg-elevated focus:ring-focused-text",
        destructive: "border-transparent bg-error hover:bg-error-hover focus:ring-focusRingError active:opacity-80 shadow-inner-button-error",
        success: "border-transparent bg-success hover:bg-success-hover focus:ring-focused-text shadow-inner-button-success"
      },
      size: {
        xxs: "w-6 h-6",
        xs: "w-8 h-8",
        sm: "w-9 h-9",
        md: "w-10 h-10",
        lg: "w-11 h-11",
        "2xl": "h-16 w-16 md:w-20 md:h-20 rounded-2xl"
      },
      active: {
        true: ""
      }
    },
    compoundVariants: [
      { variant: "primary", active: true, className: "bg-secondary" },
      { variant: "secondary", active: true, className: "bg-secondary-hover" },
      { variant: "tertiary", active: true, className: "border-2 border-primary" },
      { variant: "outline", active: true, className: "bg-color-bg-elevated" },
      { variant: "ghost", active: true, className: "bg-color-bg-elevated" },
      { variant: "destructive", active: true, className: "bg-schemes-error" },
      { variant: "success", active: true, className: "bg-success-hover" }
    ],
    defaultVariants: {
      variant: "primary",
      size: "md",
      active: false
    }
  }
);
var iconVariants2 = cva3("", {
  variants: {
    variant: {
      primary: "text-white",
      secondary: "text-text-dark",
      outline: "text-color-text",
      ghost: "text-foreground group-hover:text-background",
      destructive: "text-text-dark",
      tertiary: "text-text-light dark:text-text-dark text-text-light",
      success: "text-white"
    },
    size: {
      xxs: "w-4 h-4",
      xs: "w-4 h-4",
      sm: "w-4.5 h-4.5",
      md: "w-5 h-5",
      lg: "w-5 h-5",
      "2xl": "w-10 h-10"
    },
    active: {
      true: ""
    }
  },
  compoundVariants: [
    { variant: "primary", active: true, className: "text-white" },
    { variant: "secondary", active: true, className: "text-color-text" },
    { variant: "outline", active: true, className: "text-color-text" },
    { variant: "ghost", active: true, className: "text-background" },
    { variant: "destructive", active: true, className: "text-white" },
    { variant: "tertiary", active: true, className: "text-white" },
    { variant: "success", active: true, className: "text-white" }
  ],
  defaultVariants: {
    variant: "primary",
    size: "xxs"
  }
});
var IconButton = React4.forwardRef(
  ({ className, variant, size, icon, active, title, disabled, type = "button", ...props }) => {
    return /* @__PURE__ */ jsx10(
      "button",
      {
        className: cn(iconButtonVariants({ variant, size, active }), className, "group"),
        title,
        disabled,
        type,
        ...props,
        children: React4.cloneElement(icon, {
          className: cn(iconVariants2({ variant, size, active }))
        })
      }
    );
  }
);

// src/components/fudCard/FudCard.tsx
import { Fragment, jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
var FudCard = ({
  title,
  tag,
  handleFullScreen,
  children,
  className,
  icon,
  titleSectionClassName,
  titleStyle,
  hoverable = false,
  hasActiveBorder = false
}) => {
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      className: clsx3(
        "bg-background-wrapper-light dark:bg-background-wrapper-dark rounded-lg w-full px-4 2xl:px-8 py-7 relative dark:shadow-deep-dark shadow-deep-light",
        {
          "hover:bg-background-wrapper-light-hover dark:hover:bg-background-wrapper-dark-hover hover:scale-[1.025] transition-all duration-200 ease-in-out cursor-pointer": hoverable
        },
        className
      ),
      children: [
        /* @__PURE__ */ jsxs5("div", { className: clsx3("flex flex-row justify-between items-center mb-1 gap-4 flex-wrap", titleSectionClassName), children: [
          title && /* @__PURE__ */ jsx11("div", { className: clsx3("text-cardForeground text-2xl font-semibold whitespace-nowrap", titleStyle), children: title }),
          handleFullScreen && icon && /* @__PURE__ */ jsx11(IconButton, { icon, variant: "tertiary", size: "sm", onClick: handleFullScreen }),
          tag && /* @__PURE__ */ jsxs5("div", { className: "flex-row items-center justify-center gap-1 h-7 bg-background rounded-md px-1 pl-2 pr-1", children: [
            /* @__PURE__ */ jsx11(Text, { className: "w-fit text-cardForeground", children: tag.name }),
            /* @__PURE__ */ jsx11("div", { className: "bg-card h-5 w-5 rounded flex justify-center items-center", children: /* @__PURE__ */ jsx11(Text, { className: "text-cardForeground", children: tag.number }) })
          ] })
        ] }),
        children,
        hasActiveBorder && /* @__PURE__ */ jsxs5(Fragment, { children: [
          /* @__PURE__ */ jsx11("span", { className: "absolute inset-customBorder border-2 rounded-lg border-blue-400 animate-clippath" }),
          /* @__PURE__ */ jsx11("span", { className: "absolute inset-customBorder border-2 rounded-lg border-blue-400 animate-clippathReverse" })
        ] })
      ]
    }
  );
};

// src/components/deviceStatusDashboardCard/DeviceStatusDashboardCard.tsx
import clsx4 from "clsx";
import {
  AlertTriangle,
  CalendarIcon,
  BellIcon,
  CircleCheckIcon,
  CircleAlertIcon,
  PowerIcon,
  PowerOffIcon
} from "lucide-react";

// src/lib/colors.ts
var COLORS = {
  blue400: "#60a5fa",
  amber500: "#f59e42",
  amber600: "#d97706",
  red500: "#ef4444",
  red600: "#dc2626",
  rose400: "#fb7185",
  green500: "#22c55e",
  green600: "#16a34a",
  black: "#000000"
};

// src/components/deviceStatusDashboardCard/DeviceStatusDashboardCard.tsx
import { Fragment as Fragment2, jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
var workingStatusIconProps = {
  success: { icon: PowerIcon },
  darkGrey: { icon: PowerOffIcon },
  default: { icon: PowerOffIcon }
};
var statusIconProps = {
  success: { icon: CircleCheckIcon },
  error: { icon: BellIcon },
  warning: { icon: CircleAlertIcon },
  default: { icon: CircleAlertIcon }
};
var getWorkingStatusIcon = (variant, size = 20) => {
  const Icon = workingStatusIconProps[variant]?.icon || workingStatusIconProps.default.icon;
  return /* @__PURE__ */ jsx12(Icon, { size });
};
var getDeviceStatusIcon = (variant, size = 20) => {
  const Icon = statusIconProps[variant]?.icon || statusIconProps.default.icon;
  return /* @__PURE__ */ jsx12(Icon, { size });
};
var DeviceStatusDashboardCard = ({
  deviceName,
  tag,
  deviceSerialNumber,
  deviceUdtNumber,
  deviceStatusBadgeVariant,
  deviceStatusText,
  workingStatusBadgeVariant,
  progress,
  resursLabel,
  daysUntilExpirationUdtText,
  date,
  inspectionWarningColor,
  hasActiveBorder = false
}) => {
  return /* @__PURE__ */ jsx12(
    FudCard,
    {
      title: deviceName,
      tag,
      hoverable: true,
      hasActiveBorder,
      titleStyle: "text-3xl mb-2 flex-1",
      children: /* @__PURE__ */ jsxs6("div", { className: "flex flex-col gap-4", children: [
        /* @__PURE__ */ jsxs6("div", { className: "flex gap-2 mb-2", children: [
          /* @__PURE__ */ jsx12(Badge, { badgeSize: "lg", variant: deviceStatusBadgeVariant, fill: "outline", children: /* @__PURE__ */ jsxs6("div", { className: "flex flex-row items-center gap-2", children: [
            getDeviceStatusIcon(deviceStatusBadgeVariant),
            /* @__PURE__ */ jsx12(Text, { children: deviceStatusText })
          ] }) }),
          /* @__PURE__ */ jsx12(Badge, { variant: workingStatusBadgeVariant, badgeSize: "lg", fill: "outline", children: /* @__PURE__ */ jsx12("div", { className: "flex flex-row items-center gap-2", children: getWorkingStatusIcon(workingStatusBadgeVariant) }) })
        ] }),
        /* @__PURE__ */ jsxs6("div", { className: "flex flex-col gap-1", children: [
          /* @__PURE__ */ jsx12(Text, { className: "text-infoGray", children: `S.N: ${deviceSerialNumber}` }),
          /* @__PURE__ */ jsx12(Text, { className: "text-infoGray", children: `UDT number: ${deviceUdtNumber}` })
        ] }),
        /* @__PURE__ */ jsx12("div", { className: "h-0.25 bg-color-divider" }),
        /* @__PURE__ */ jsx12("div", { className: "flex flex-col gap-3", children: /* @__PURE__ */ jsx12(ProgressBar, { progress, label: resursLabel, unit: "%", isStatusCard: true }) }),
        daysUntilExpirationUdtText && /* @__PURE__ */ jsxs6(Fragment2, { children: [
          /* @__PURE__ */ jsx12("div", { className: "h-0.25 bg-color-divider" }),
          /* @__PURE__ */ jsxs6(
            Text,
            {
              className: clsx4("flex gap-4 items-center px-3 py-2 rounded-md border", {
                "text-amber-400 dark:bg-amber-950/40 bg-amber-950 border-amber-800/50": inspectionWarningColor === "warning",
                "text-rose-400 dark:bg-red-950/40 bg-red-950 border-red-800/50": inspectionWarningColor === "error"
              }),
              children: [
                inspectionWarningColor && /* @__PURE__ */ jsx12(
                  AlertTriangle,
                  {
                    color: inspectionWarningColor === "error" ? COLORS.rose400 : COLORS.amber500,
                    className: "inline-block mr-1",
                    size: 30
                  }
                ),
                daysUntilExpirationUdtText
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx12("div", { className: "h-0.25 bg-color-divider" }),
        /* @__PURE__ */ jsxs6(Text, { className: "text-sm flex gap-2 items-center font-medium text-foreground", children: [
          /* @__PURE__ */ jsx12(CalendarIcon, { size: 20, color: COLORS.blue400 }),
          date
        ] })
      ] })
    }
  );
};

// src/components/alert/Alert.tsx
import * as Slot from "@rn-primitives/slot";
import { View as View5, Text as Text3 } from "react-native";
import { styled as styled2 } from "nativewind";
import { jsx as jsx13, jsxs as jsxs7 } from "react/jsx-runtime";
var Alert = ({
  badgeVariant,
  badgeInfoText,
  alertInfo,
  deviceName,
  className,
  asChild,
  ...props
}) => {
  const Component = asChild ? Slot.View : View5;
  const StyledView = styled2(View5);
  const StyledText = styled2(Text3);
  const StyledComponent = styled2(Component);
  return /* @__PURE__ */ jsxs7(
    StyledComponent,
    {
      className: cn("bg-background-grey h-13 rounded-md", className),
      ...props,
      children: [
        /* @__PURE__ */ jsxs7(StyledView, { className: "bg-background flex-row justify-between rounded-md pl-2 items-center", children: [
          /* @__PURE__ */ jsx13(StyledText, { className: "text-foreground text-xs leading-none", children: deviceName }),
          /* @__PURE__ */ jsx13(Badge, { variant: badgeVariant, children: badgeInfoText })
        ] }),
        /* @__PURE__ */ jsx13(StyledView, { className: "pl-2 flex-1 justify-center", children: /* @__PURE__ */ jsx13(StyledText, { className: "text-foreground text-xs leading-none", children: alertInfo }) })
      ]
    }
  );
};

// src/components/menu/Menu.tsx
import React5, { useState } from "react";

// src/components/companyMenuButton/CompanyMenuButton.tsx
import { clsx as clsx5 } from "clsx";
import { jsx as jsx14, jsxs as jsxs8 } from "react/jsx-runtime";
var CompanyMenuButton = ({
  title,
  children,
  active,
  icon,
  ...props
}) => {
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      className: clsx5(
        "flex flex-row bg-primary rounded-md items-center px-4 py-2 justify-between hover:bg-primary-hover cursor-pointer shadow-inner-button-primary",
        { "!bg-primary-hover border-2 border-primary-border": active }
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx14("span", { className: cn({ "leading-special": active }, "text-text-dark leading-companyButton"), children: title }),
        /* @__PURE__ */ jsx14("span", { className: cn(active && "rotate-180"), children: icon }),
        children
      ]
    }
  );
};

// src/components/menu/Menu.tsx
import { Fragment as Fragment3, jsx as jsx15, jsxs as jsxs9 } from "react/jsx-runtime";
var Menu = ({ menuItems, handleNavigate, nestedPaths, className }) => {
  const [subMenuIndex, setSubMenuIndex] = useState(-1);
  const [activeIndex, setActiveIndex] = useState(0);
  const [deviceIndex, setDeviceIndex] = useState(-1);
  const handleMainButtonClick = (index, path) => {
    if (activeIndex === index) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(index);
    setSubMenuIndex(-1);
    setDeviceIndex(-1);
    if (path) {
      handleNavigate(path);
    }
  };
  const handleSubItemClick = (subIndex, path) => {
    if (subMenuIndex === subIndex) {
      setSubMenuIndex(-1);
      return;
    }
    setSubMenuIndex(subIndex);
    setDeviceIndex(-1);
    if (path) {
      handleNavigate(path);
    }
  };
  const handleDeviceClick = (index, path, id) => {
    setDeviceIndex(index);
    if (path) {
      handleNavigate(`${path}/${id}`);
    }
  };
  return /* @__PURE__ */ jsx15("div", { className: cn("flex flex-col gap-2 items-start rounded-2xl", className), children: menuItems.map((item, menuIndex) => /* @__PURE__ */ jsxs9(React5.Fragment, { children: [
    /* @__PURE__ */ jsx15(
      Button,
      {
        variant: "primary",
        size: "mdFull",
        onClick: () => handleMainButtonClick(menuIndex, item.path),
        active: activeIndex === menuIndex,
        icon: item.icon,
        children: item.title
      }
    ),
    activeIndex === menuIndex && Array.isArray(item.subItems) && item.subItems.length > 0 && /* @__PURE__ */ jsx15("div", { className: "flex flex-col pl-2 gap-2 w-full", children: item.subItems.map((subItem, subIndex) => /* @__PURE__ */ jsx15(React5.Fragment, { children: nestedPaths?.includes(item.title) ? /* @__PURE__ */ jsxs9(Fragment3, { children: [
      /* @__PURE__ */ jsx15(
        CompanyMenuButton,
        {
          active: subIndex === subMenuIndex,
          onClick: () => handleSubItemClick(subIndex, subItem.path),
          icon: subItem.icon,
          title: subItem.title
        }
      ),
      subIndex === subMenuIndex && subItem.devices && subItem.devices.length > 0 && /* @__PURE__ */ jsx15("div", { className: "flex flex-col pl-4 gap-2", children: subItem.devices.map(({ path, id, text }, index) => /* @__PURE__ */ jsx15(
        Button,
        {
          variant: "primary",
          size: "mdFull",
          onClick: () => handleDeviceClick(index, path, id),
          active: deviceIndex === index,
          children: text
        },
        id
      )) })
    ] }) : /* @__PURE__ */ jsx15(
      Button,
      {
        variant: "primary",
        size: "mdFull",
        onClick: () => handleSubItemClick(subIndex, subItem.path),
        icon: subItem.icon,
        active: subMenuIndex === subIndex,
        children: subItem.title
      }
    ) }, subItem.title)) })
  ] }, item.title)) });
};

// src/components/serviceExpiryCard/ServiceExpiryCard.tsx
import { AlertTriangle as AlertTriangle2 } from "lucide-react";
import { jsx as jsx16, jsxs as jsxs10 } from "react/jsx-runtime";
var ServiceExpiryCard = ({
  mainText,
  daysUntilExpirationUdtText,
  inspectionWarningColor,
  className
}) => {
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      className: cn(
        "w-full bg-background-wrapper-light dark:bg-background-wrapper-dark rounded-lg flex flex-row 2xl:px-8 px-4 items-center justify-between shadow-deep-light dark:shadow-deep-dark",
        className
      ),
      children: [
        /* @__PURE__ */ jsx16(Text, { className: "text-2xl font-semibold leading-none text-color-lightGreen", children: mainText }),
        /* @__PURE__ */ jsx16(Badge, { badgeSize: "xl", variant: "darkGrey", children: daysUntilExpirationUdtText }),
        inspectionWarningColor && /* @__PURE__ */ jsx16(
          AlertTriangle2,
          {
            color: inspectionWarningColor === "error" ? "#df1e1e" : "#ecb529",
            size: 50,
            className: "inline-block mr-1"
          }
        )
      ]
    }
  );
};

// src/components/iconsContainer/IconsContainer.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var IconsContainer = ({ buttons, className }) => {
  return /* @__PURE__ */ jsx17(
    "div",
    {
      className: cn(
        "w-full gap-8.5 flex flex-row items-center bg-background-wrapper-light dark:bg-background-wrapper-dark h-20 rounded-lg p-5",
        className
      ),
      children: buttons.map((button, id) => /* @__PURE__ */ jsx17(IconButton, { size: "sm", variant: "secondary", icon: button.icon, onClick: button.onClick }, id))
    }
  );
};

// src/components/web/weatherPanel/WeatherPanel.tsx
import { Wind, CloudRainWind, Droplets, Thermometer } from "lucide-react";
import { jsx as jsx18, jsxs as jsxs11 } from "react/jsx-runtime";
var WeatherPanel = ({ weatherData }) => {
  return /* @__PURE__ */ jsxs11("div", { className: "w-full bg-background-wrapper-light dark:bg-background-wrapper-dark rounded-lg py-3 md:py-5 2xl:px-8 px-4 text-xs md:text-md shadow-deep-light dark:shadow-deep-dark flex-1", children: [
    weatherData.locationName && /* @__PURE__ */ jsxs11("div", { className: "mb-3 text-center text-muted-foreground", children: [
      weatherData.locationName,
      weatherData.country && `, ${weatherData.country}`
    ] }),
    /* @__PURE__ */ jsxs11("div", { className: "tracking-tighter flex justify-between items-center w-full", children: [
      /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-1 sm:gap-2", children: [
        /* @__PURE__ */ jsx18(Wind, { size: "20" }),
        /* @__PURE__ */ jsxs11("div", { className: "flex flex-col justify-center", children: [
          /* @__PURE__ */ jsxs11("span", { className: "leading-none", children: [
            `${weatherData.windSpeed}km/h`,
            " "
          ] }),
          /* @__PURE__ */ jsxs11("span", { className: "whitespace-nowrap leading-none text-infoGray", children: [
            " ",
            weatherData.windDirection
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-1 sm:gap-2", children: [
        /* @__PURE__ */ jsx18(CloudRainWind, { size: "20" }),
        `${weatherData.precipitation}%`
      ] }),
      /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-1 sm:gap-2", children: [
        /* @__PURE__ */ jsx18(Droplets, { size: "20" }),
        `${weatherData.humidity}%`
      ] }),
      /* @__PURE__ */ jsxs11("div", { className: "flex items-center gap-1 sm:gap-2", children: [
        /* @__PURE__ */ jsx18(Thermometer, { size: "20" }),
        `${weatherData.temperature}\xB0C`
      ] })
    ] })
  ] });
};

// src/components/web/select/Select.tsx
import React7 from "react";
import { ChevronDown } from "lucide-react";

// src/components/ui/shadcnComponents/popover.tsx
import * as React6 from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx19 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverContent = React6.forwardRef(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx19(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx19(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 rounded-md bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/components/web/select/Select.tsx
import { Fragment as Fragment4, jsx as jsx20, jsxs as jsxs12 } from "react/jsx-runtime";
var Select = ({
  className,
  contentClassName,
  items,
  selected,
  setSelected,
  renderItem,
  renderSelected
}) => {
  const [open, setOpen] = React7.useState(false);
  return /* @__PURE__ */ jsxs12(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsxs12(
      PopoverTrigger,
      {
        className: cn(
          "flex w-full items-center justify-between h-10 px-3 py-2 rounded-md bg-background-gray-badge-light dark:bg-background-gray-badge hover:dark:bg-background-gray-badge-hover hover:bg-background-gray-badge-light-hover text-text-light dark:text-text-dark cursor-pointer shadow-deep-light dark:shadow-deep-dark",
          className
        ),
        onClick: () => setOpen(!open),
        children: [
          renderSelected ? renderSelected(selected) : /* @__PURE__ */ jsxs12("span", { className: "flex items-center gap-2", children: [
            selected.icon && /* @__PURE__ */ jsx20("img", { src: selected.icon, alt: selected.name || selected.id, className: "w-5 h-5" }),
            /* @__PURE__ */ jsx20(Text2, { className: "text-infoGray text-sm", children: selected.name || selected.id })
          ] }),
          /* @__PURE__ */ jsx20(ChevronDown, { color: "#878B95" })
        ]
      }
    ),
    /* @__PURE__ */ jsx20(
      PopoverContent,
      {
        align: "start",
        className: cn(
          "w-popover-trigger rounded-md p-2 bg-background-gray-badge-light dark:bg-background-gray-badge",
          contentClassName
        ),
        children: items.map((item) => /* @__PURE__ */ jsx20(
          "div",
          {
            onClick: () => {
              setSelected(item);
              setOpen(false);
            },
            className: "px-2 py-2 hover:bg-infoGray hover:dark:bg-background-gray-badge-hover hover:bg-background-gray-badge-light-hover text-text-light dark:text-text-dark text-text-light dark:text-text-dark rounded-md cursor-pointer flex items-center gap-2",
            children: renderItem ? renderItem(item) : /* @__PURE__ */ jsxs12(Fragment4, { children: [
              item.icon && /* @__PURE__ */ jsx20("img", { src: item.icon, alt: item.name || item.id, className: "w-5 h-5" }),
              /* @__PURE__ */ jsx20("span", { children: item.name || item.id })
            ] })
          },
          item.id
        ))
      }
    )
  ] });
};

// src/components/infoTabsStatsCard/InfoTabsStatsCard.tsx
import { jsx as jsx21, jsxs as jsxs13 } from "react/jsx-runtime";
var InfoTabsStatsCard = ({
  label,
  value,
  isStatisticButton = true,
  additionalInfoIcon,
  chartLineIcon,
  onChartLinePress,
  className
}) => {
  return /* @__PURE__ */ jsxs13(
    "div",
    {
      className: cn(
        "p-4 bg-background-card text-text-dark flex flex-row justify-between rounded-lg gap-2 max-w-xs",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs13("div", { className: "flex-1 gap-2 flex flex-col", children: [
          /* @__PURE__ */ jsx21(Text, { className: "text-rockBlue mb-2 flex-wrap", children: label }),
          /* @__PURE__ */ jsx21(Text, { className: cn("text-info-text-dark text-2xl font-semibold", additionalInfoIcon && "mb-2"), children: value })
        ] }),
        isStatisticButton && /* @__PURE__ */ jsx21(IconButton, { variant: "secondary", size: "sm", icon: chartLineIcon, onClick: onChartLinePress })
      ]
    }
  );
};

// src/components/tabsBar/TabsBar.tsx
import React11 from "react";
import clsx6 from "clsx";

// src/components/ui/shadcnComponents/tabsBar.tsx
import * as React8 from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { jsx as jsx22 } from "react/jsx-runtime";
var Tabs = TabsPrimitive.Root;
var TabsList = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx22(
  TabsPrimitive.List,
  {
    ref,
    className: cn(
      "inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-infoGray shadow-deep-light dark:shadow-deep-dark",
      className
    ),
    ...props
  }
));
TabsList.displayName = TabsPrimitive.List.displayName;
var TabsTrigger = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx22(
  TabsPrimitive.Trigger,
  {
    ref,
    className: cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded px-3 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:px-10 data-[state=active]:bg-background-tab-element data-[state=active]:text-text data-[state=active]:shadow-inner-button-dark",
      className
    ),
    ...props
  }
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
var TabsContent = React8.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx22(
  TabsPrimitive.Content,
  {
    ref,
    className: cn(
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    ),
    ...props
  }
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

// src/components/input/Input.tsx
import * as React9 from "react";
import { jsx as jsx23 } from "react/jsx-runtime";
var Input = React9.forwardRef(({ className, type, error, ...props }, ref) => {
  return /* @__PURE__ */ jsx23(
    "input",
    {
      type,
      className: cn(
        "w-full h-9 text-infoGray placeholder:text-infoGray px-3 bg-card rounded-md text-sm font-normal focus:outline-none",
        { "border border-red-500 focus:border-red-500": error },
        { "mb-4": !error },
        className
      ),
      ref,
      ...props
    }
  );
});
Input.displayName = "Input";

// src/components/ui/shadcnComponents/scrollArea.tsx
import * as React10 from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import { jsx as jsx24, jsxs as jsxs14 } from "react/jsx-runtime";
var ScrollArea = React10.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs14(ScrollAreaPrimitive.Root, { ref, className: cn("relative overflow-hidden", className), ...props, children: [
  /* @__PURE__ */ jsx24(ScrollAreaPrimitive.Viewport, { className: "h-full w-full rounded-[inherit]", children }),
  /* @__PURE__ */ jsx24(ScrollBar, {}),
  /* @__PURE__ */ jsx24(ScrollAreaPrimitive.Corner, {})
] }));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;
var ScrollBar = React10.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx24(
  ScrollAreaPrimitive.ScrollAreaScrollbar,
  {
    ref,
    forceMount: true,
    orientation,
    className: cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx24(ScrollAreaPrimitive.ScrollAreaThumb, { className: "relative flex-1 rounded-full bg-foreground" })
  }
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

// src/components/tabsBar/TabsBar.tsx
import { jsx as jsx25, jsxs as jsxs15 } from "react/jsx-runtime";
var TabsBar = React11.forwardRef(
  ({ tabsData, onTabChange, activeTab, editedTab, contentHeight, className, overflow }, ref) => {
    const [editedTabName, setEditedTabName] = React11.useState(editedTab?.title || "");
    React11.useImperativeHandle(ref, () => ({
      getInputValue: () => editedTabName
    }));
    const handleTabChange = (value) => {
      if (onTabChange) {
        onTabChange(value);
      }
    };
    return /* @__PURE__ */ jsxs15(Tabs, { className: "!text-text-dark", value: activeTab, onValueChange: handleTabChange, children: [
      /* @__PURE__ */ jsxs15(ScrollArea, { className: "mb-4 p-0 rounded-md", children: [
        /* @__PURE__ */ jsx25("div", { className: "flex justify-center ", children: /* @__PURE__ */ jsx25(TabsList, { className: "bg-background-gray-badge shadow-inner-button rounded-md h-13 p-1 flex justify-center", children: tabsData?.map(({ title, section }) => /* @__PURE__ */ jsx25(React11.Fragment, { children: section === editedTab?.section ? /* @__PURE__ */ jsx25(
          Input,
          {
            value: editedTabName,
            className: "!mb-0 w-45.75",
            onChange: (e) => setEditedTabName(e.target.value)
          }
        ) : /* @__PURE__ */ jsx25(TabsTrigger, { value: section, className, children: title }) }, section)) }) }),
        /* @__PURE__ */ jsx25(ScrollBar, { orientation: "horizontal" })
      ] }),
      /* @__PURE__ */ jsx25("div", { className: clsx6("w-full flex flex-col", overflow, contentHeight), children: tabsData?.map(({ section, content }) => /* @__PURE__ */ jsx25(TabsContent, { value: section, children: content }, `section-${section}`)) })
    ] });
  }
);

// src/components/rtStatus/RtStatus.tsx
import { jsx as jsx26, jsxs as jsxs16 } from "react/jsx-runtime";
var RtStatus = ({ label, icon }) => {
  return /* @__PURE__ */ jsxs16("div", { className: "flex flex-row items-center gap-4 text-text-light dark:text-text-dark", children: [
    /* @__PURE__ */ jsx26("span", { children: icon }),
    /* @__PURE__ */ jsx26("span", { className: "text-xs leading-none break-words", style: { overflowWrap: "anywhere" }, children: label })
  ] });
};

// src/components/helpAlert/HelpAlert.tsx
import * as Slot2 from "@rn-primitives/slot";
import { View as View7, Text as Text4 } from "react-native";
import { styled as styled3 } from "nativewind";
import { jsx as jsx27, jsxs as jsxs17 } from "react/jsx-runtime";
var HelpAlert = ({
  icon,
  className,
  asChild,
  handleButton,
  handleIconButton,
  firstParagraph,
  secondParagraph,
  buttonText,
  ...props
}) => {
  const Component = asChild ? Slot2.View : View7;
  const StyledComponent = styled3(Component);
  const StyledText = styled3(Text4);
  return /* @__PURE__ */ jsxs17(StyledComponent, { className: "bg-card p-4 rounded-2xl w-full bg-cover", ...props, children: [
    /* @__PURE__ */ jsx27(StyledText, { className: "text-lg text-text font-bold", children: firstParagraph }),
    /* @__PURE__ */ jsx27(StyledText, { className: "text-xs text-text mb-3.5", children: secondParagraph }),
    /* @__PURE__ */ jsx27(Button, { variant: "primary", size: "mdFull", onClick: handleButton, className: "justify-center", children: /* @__PURE__ */ jsxs17(Text4, { children: [
      " ",
      buttonText
    ] }) })
  ] });
};

// src/components/weatherAlert/WeatherAlert.tsx
import { View as View8 } from "react-native";
import * as Slot3 from "@rn-primitives/slot";
import { styled as styled4 } from "nativewind";
import { jsx as jsx28, jsxs as jsxs18 } from "react/jsx-runtime";
var WeatherAlert = ({ xIcon, handleXIcon, title, cloudLightningIcon, description, asChild }) => {
  const Component = asChild ? Slot3.View : View8;
  const StyledComponent = styled4(Component);
  const StyledView = styled4(View8);
  return /* @__PURE__ */ jsxs18(StyledComponent, { className: "bg-card rounded-2xl w-full px-8 py-7", children: [
    /* @__PURE__ */ jsxs18(StyledView, { className: "flex flex-row justify-between text-foreground mb-3", children: [
      /* @__PURE__ */ jsxs18(StyledView, { className: "flex flex-row gap-2", children: [
        cloudLightningIcon,
        /* @__PURE__ */ jsx28(Text, { className: "font-medium text-sm", children: title })
      ] }),
      /* @__PURE__ */ jsx28(IconButton, { icon: xIcon, size: "xxs", variant: "ghost", onClick: handleXIcon })
    ] }),
    /* @__PURE__ */ jsx28(Text, { className: "text-primaryBadge-text text-sm", children: description })
  ] });
};

// src/components/eventNotification/EventNotification.tsx
import { View as View10 } from "react-native";
import * as Slot4 from "@rn-primitives/slot";
import { styled as styled5 } from "nativewind";
import { jsx as jsx29, jsxs as jsxs19 } from "react/jsx-runtime";
var EventNotification = ({
  xIcon,
  statusIndicatorIcon,
  handleXIcon,
  handleButton,
  eventTitle,
  eventText,
  deviceNumber,
  timestamp,
  buttonText,
  asChild
}) => {
  const Component = asChild ? Slot4.View : View10;
  const StyledComponent = styled5(Component);
  const StyledView = styled5(View10);
  return /* @__PURE__ */ jsxs19(StyledComponent, { className: "bg-card rounded-2xl w-full px-8 py-7 shadow-alertShadow", children: [
    /* @__PURE__ */ jsx29(Text, { className: "text-2xl font-semibold mb-4.25", children: eventTitle }),
    /* @__PURE__ */ jsxs19(StyledView, { className: "flex flex-row items-center justify-between mb-8", children: [
      /* @__PURE__ */ jsxs19(StyledView, { className: "flex gap-2", children: [
        /* @__PURE__ */ jsx29(Text, { className: "text-sm font-medium leading-none", children: eventText }),
        /* @__PURE__ */ jsx29(Text, { className: "text-sm text-infoGray leading-none", children: deviceNumber }),
        /* @__PURE__ */ jsx29(Text, { className: "text-sm text-infoGray leading-none", children: timestamp })
      ] }),
      /* @__PURE__ */ jsx29(IconButton, { onClick: handleXIcon, icon: xIcon, size: "xxs", variant: "ghost" })
    ] }),
    /* @__PURE__ */ jsx29(StyledView, { className: "flex items-center", children: /* @__PURE__ */ jsxs19(Button, { onClick: handleButton, children: [
      " ",
      buttonText
    ] }) }),
    /* @__PURE__ */ jsx29(StyledView, { className: "absolute right-2.5 top-5", children: statusIndicatorIcon })
  ] });
};

// src/components/monitoringLayoutManager/MonitoringLayoutManager.tsx
import * as Slot5 from "@rn-primitives/slot";
import { styled as styled6 } from "nativewind";
import { View as View13 } from "react-native";
import { jsx as jsx30, jsxs as jsxs20 } from "react/jsx-runtime";
var MonitoringLayoutManager = ({
  asChild,
  text,
  gripVerticalIcon,
  layoutGridIcon,
  grid3x3Icon,
  camsPerRow,
  handleSetLayout
}) => {
  const Component = asChild ? Slot5.View : View13;
  const StyledComponent = styled6(Component);
  return /* @__PURE__ */ jsxs20(StyledComponent, { className: "flex-row gap-4 items-center", children: [
    /* @__PURE__ */ jsx30(Text, { className: "text-sm font-medium text-infoGray", children: text }),
    /* @__PURE__ */ jsx30(
      IconButton,
      {
        size: "sm",
        variant: "ghost",
        onClick: () => handleSetLayout(12),
        icon: gripVerticalIcon,
        active: camsPerRow === 12
      }
    ),
    /* @__PURE__ */ jsx30(
      IconButton,
      {
        size: "sm",
        variant: "ghost",
        onClick: () => handleSetLayout(8),
        icon: layoutGridIcon,
        active: camsPerRow === 8
      }
    ),
    /* @__PURE__ */ jsx30(
      IconButton,
      {
        size: "sm",
        variant: "ghost",
        onClick: () => handleSetLayout(6),
        active: camsPerRow === 6,
        icon: grid3x3Icon
      }
    )
  ] });
};

// src/components/mapMarker/MapMarker.tsx
import clsx7 from "clsx";
import { PowerOffIcon as PowerOffIcon2 } from "lucide-react";
import { Fragment as Fragment5, jsx as jsx31, jsxs as jsxs21 } from "react/jsx-runtime";
var MapMarker = ({ deviceStatus, deviceName, isClicked }) => {
  const { alertStatus, isWorking } = deviceStatus;
  return /* @__PURE__ */ jsxs21(
    "div",
    {
      className: clsx7(
        "relative w-fit p-3 text-3.5 leading-companyButton whitespace-nowrap rounded-md hover:scale-[1.1] transition-all duration-200 ease-in-out cursor-pointer",
        {
          "bg-warning hover:bg-warning-hover !text-black shadow-inner-button-warning": alertStatus === "error",
          "bg-error hover:bg-error-hover shadow-inner-button-warning": alertStatus === "alarm",
          "bg-success hover:bg-success-hover !text-black shadow-inner-button-success": alertStatus === "normal",
          "p-5": isClicked
        }
      ),
      children: [
        /* @__PURE__ */ jsxs21("div", { className: "flex gap-2 items-center", children: [
          deviceName,
          !isWorking && /* @__PURE__ */ jsx31(PowerOffIcon2, { size: 16 })
        ] }),
        isClicked && /* @__PURE__ */ jsxs21(Fragment5, { children: [
          /* @__PURE__ */ jsx31("span", { className: "absolute inset-customBorder border-2 rounded-md border-info-border animate-clippath" }),
          /* @__PURE__ */ jsx31("span", { className: "absolute inset-customBorder border-2 rounded-md border-info-border animate-clippathReverse" })
        ] }),
        /* @__PURE__ */ jsx31(
          "div",
          {
            className: clsx7(
              "absolute right-3 transform -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent",
              {
                "border-t-warning": alertStatus === "error",
                "border-t-error": alertStatus === "alarm",
                "border-t-success": alertStatus === "normal",
                "!border-t-info-text": alertStatus
              }
            )
          }
        )
      ]
    }
  );
};

// src/components/hookControlCard/HookControlCard.tsx
import { Fragment as Fragment6, jsx as jsx32, jsxs as jsxs22 } from "react/jsx-runtime";
var HookControlCard = ({
  label,
  value,
  unit,
  chevronUpIcon,
  rotateCwIcon,
  up = false,
  down = false,
  left = false,
  right = false,
  rotate = false,
  showRotateAndArrows = true,
  scrollTargetId,
  className
}) => {
  return /* @__PURE__ */ jsxs22(
    "div",
    {
      className: cn(
        "w-45.75 h-35 flex flex-col justify-between bg-background-card rounded-lg p-2 text-text-dark",
        {
          "cursor-pointer hover:bg-background-card-hover transition-all duration-200 ease-in-out shadow-inner-button": scrollTargetId
        },
        className
      ),
      children: [
        /* @__PURE__ */ jsx32("div", { className: "text-lg leading-none text-wrap mb-2", children: label }),
        /* @__PURE__ */ jsxs22("div", { className: "flex flex-row justify-between items-end", children: [
          /* @__PURE__ */ jsx32("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx32("div", { className: "bg-secondary text-info-text-dark px-2 rounded-md w-fit flex items-end", children: `${value}${unit}` }) }),
          /* @__PURE__ */ jsxs22("div", { className: "w-20 h-20 relative flex justify-center items-center p-5.5", children: [
            /* @__PURE__ */ jsx32(
              "div",
              {
                className: cn(
                  "absolute top-0 w-6 h-6 flex items-center justify-center rounded-md bg-secondary border border-secondary-border",
                  { "bg-success-border border-success-text": up, "top-4": !showRotateAndArrows }
                ),
                children: chevronUpIcon
              }
            ),
            /* @__PURE__ */ jsx32(
              "div",
              {
                className: cn(
                  "absolute bottom-0 rotate-180 w-6 h-6 flex items-center justify-center rounded-md bg-secondary border border-secondary-border",
                  { "bg-success-border border-success-text": down }
                ),
                children: chevronUpIcon
              }
            ),
            showRotateAndArrows && /* @__PURE__ */ jsxs22(Fragment6, { children: [
              /* @__PURE__ */ jsx32(
                "div",
                {
                  className: cn(
                    "absolute left-0 -rotate-90 w-6 h-6 flex items-center justify-center rounded-md bg-secondary border border-secondary-border",
                    { "bg-success-border border-success-text": left }
                  ),
                  children: chevronUpIcon
                }
              ),
              /* @__PURE__ */ jsx32(
                "div",
                {
                  className: cn(
                    "absolute right-0 rotate-90 w-6 h-6 flex items-center justify-center rounded-md bg-secondary border border-secondary-border",
                    { "bg-success-border border-success-text": right }
                  ),
                  children: chevronUpIcon
                }
              ),
              /* @__PURE__ */ jsx32(
                "div",
                {
                  className: cn(
                    "absolute left-7 top-7 w-6 h-6 flex items-center justify-center rounded-md bg-secondary border border-secondary-border",
                    { "bg-success-border border-success-text": rotate }
                  ),
                  children: rotateCwIcon
                }
              )
            ] })
          ] })
        ] })
      ]
    }
  );
};

// src/components/craneControlCard/CraneControlCard.tsx
import { Fragment as Fragment7, jsx as jsx33, jsxs as jsxs23 } from "react/jsx-runtime";
var CraneControlCard = ({
  label,
  chevronUpIcon,
  up = false,
  down = false,
  left = false,
  right = false,
  direction = "horizontal",
  arrowPosition = "straight",
  scrollTargetId,
  className
}) => {
  const isDiagonal = direction === "vertical" && arrowPosition === "diagonal";
  const topIconStyle = isDiagonal ? { transform: "rotate(30deg)", transformOrigin: "54% 51%" } : void 0;
  const bottomIconStyle = isDiagonal ? { transform: "rotate(210deg)", transformOrigin: "49% 52%" } : { transform: "rotate(180deg)", transformOrigin: "50% 50%" };
  return /* @__PURE__ */ jsxs23(
    "div",
    {
      className: cn(
        "w-45.75 flex flex-col rounded-lg p-2 max-h-28 h-28 bg-background-card text-text-dark",
        scrollTargetId && "hover:bg-background-card-hover cursor-pointer transition-all duration-200 ease-in-out shadow-inner-button",
        className
      ),
      children: [
        /* @__PURE__ */ jsx33("div", { className: "text-lg flex-1 leading-none", children: label }),
        /* @__PURE__ */ jsx33("div", { className: "min-w-min flex flex-row gap-5 justify-center items-center flex-1", children: direction === "horizontal" ? /* @__PURE__ */ jsxs23(Fragment7, { children: [
          /* @__PURE__ */ jsx33(
            "div",
            {
              className: cn(
                "w-6 h-6 flex items-center justify-center rounded-md -rotate-90 bg-secondary border border-secondary-border",
                left && "bg-success-border border-success-text"
              ),
              children: chevronUpIcon
            }
          ),
          /* @__PURE__ */ jsx33(
            "div",
            {
              className: cn(
                "w-6 h-6 flex items-center justify-center rounded-md rotate-90 bg-secondary border border-secondary-border",
                right && "bg-success-border border-success-text"
              ),
              children: chevronUpIcon
            }
          )
        ] }) : /* @__PURE__ */ jsxs23(Fragment7, { children: [
          /* @__PURE__ */ jsx33(
            "div",
            {
              className: cn(
                "w-6 h-6 flex items-center justify-center rounded-md bg-secondary border border-secondary-border",
                up && "bg-success-border border-success-text"
              ),
              children: /* @__PURE__ */ jsx33("span", { style: topIconStyle, className: "flex items-center justify-center w-full h-full", children: chevronUpIcon })
            }
          ),
          /* @__PURE__ */ jsx33(
            "div",
            {
              className: cn(
                "w-6 h-6 flex items-center justify-center rounded-md bg-secondary border border-secondary-border",
                down && "bg-success-border border-success-text"
              ),
              children: /* @__PURE__ */ jsx33("span", { style: bottomIconStyle, className: "flex items-center justify-center w-full h-full", children: chevronUpIcon })
            }
          )
        ] }) })
      ]
    }
  );
};
export {
  Alert,
  Badge,
  Button,
  Card,
  CraneControlCard,
  DeviceStatusDashboardCard,
  EventNotification,
  FudCard,
  HelpAlert,
  HookControlCard,
  IconButton,
  IconsContainer,
  InfoTabsStatsCard,
  Input,
  MapMarker,
  Menu,
  MonitoringLayoutManager,
  ProgressBar,
  RtStatus,
  SearchInput,
  Select,
  ServiceExpiryCard,
  StatsCard,
  TabsBar,
  Text2 as Text,
  TextClassContext2 as TextClassContext,
  ToggleSwitch,
  WeatherAlert,
  WeatherPanel,
  badgeTextVariants,
  badgeVariants,
  buttonTextVariants,
  buttonVariants,
  iconButtonVariants,
  iconVariants2 as iconVariants
};
//# sourceMappingURL=index.mjs.map